import { toast as v3Toast, Toast as HeroUIToast } from "@heroui/react";
import type { ButtonProps } from "@heroui/react";
import type { ReactNode } from "react";

type V3Variant = "default" | "accent" | "success" | "warning" | "danger";

const colorToVariant: Record<string, V3Variant> = {
  default: "default",
  primary: "accent",
  secondary: "default",
  success: "success",
  warning: "warning",
  danger: "danger",
};

export interface AddToastOptions {
  title?: ReactNode;
  description?: ReactNode;
  color?: "default" | "primary" | "secondary" | "success" | "warning" | "danger";
  /** v3 native action button rendered in the toast's action slot. */
  actionProps?: ButtonProps;
  /**
   * In v2 this could be "solid" | "flat" | "bordered" | etc.
   * In v3 it must be one of V3Variant. When passed an unknown value
   * we fall back to mapping from `color` (or undefined).
   */
  variant?: V3Variant | string;
  timeout?: number;
  onClose?: () => void;
  // -------- Accepted-but-ignored v2 props (no v3 equivalent yet) --------
  // TODO(v3): no `severity` in v3 — relied on `color`/`variant` only.
  severity?: string;
  // TODO(v3): no `hideIcon` in v3 — use compound `Toast.Indicator` for custom rendering.
  hideIcon?: boolean;
  // TODO(v3): no `classNames` slot map on `toast()` — must customize via compound API.
  classNames?: Record<string, string>;
  // TODO(v3): no `size` on `toast()` — must customize via compound API / theme.
  size?: string;
  // TODO(v3): no `endContent` on `toast()` — use `actionProps` (a `ButtonProps`) instead.
  endContent?: ReactNode;
  // TODO(v3): no `promise` on `addToast` — use `toast.promise(p, { loading, success, error })`.
  promise?: Promise<unknown>;
  // Allow unknown extras so v2 callsites don't fail typecheck.
  [extra: string]: unknown;
}

function resolveVariant(opts: AddToastOptions): V3Variant | undefined {
  const allowed: V3Variant[] = [
    "default",
    "accent",
    "success",
    "warning",
    "danger",
  ];

  if (opts.variant && allowed.includes(opts.variant as V3Variant)) {
    return opts.variant as V3Variant;
  }

  if (opts.color && opts.color in colorToVariant) {
    return colorToVariant[opts.color];
  }

  return undefined;
}

/**
 * v2-compatible shim around the v3 `toast()` function.
 *
 * Keeps the `addToast({ title, description, color, ... })` signature so the
 * call sites do not have to change. Extra v2-only props are accepted and
 * ignored (see TODOs above).
 */
export function addToast(opts: AddToastOptions): string {
  const { title, description, timeout, onClose, actionProps } = opts;

  return v3Toast(title ?? "", {
    description,
    variant: resolveVariant(opts),
    timeout: timeout === Infinity ? undefined : timeout,
    onClose,
    actionProps,
  });
}

/** v2 `closeToast(key)` shim — v3 exposes `toast.close(key)`. */
export function closeToast(key: string): void {
  v3Toast.close(key);
}

/** v2 `closeAll()` shim — v3 exposes `toast.clear()`. */
export function closeAll(): void {
  v3Toast.clear();
}

/**
 * Re-export of v3's `Toast.Provider` under the v2 name so existing
 * `<ToastProvider />` JSX keeps working.
 *
 * Note: v2 `placement="bottom-right"`, `toastOffset`, and `toastProps`
 * are NOT supported in v3 with the same shape. Adjust at the call site
 * in `src/layouts/root.tsx`.
 */
export const ToastProvider = HeroUIToast.Provider;
